import { FEATURE_FLAGS } from '@highlight/analytics'
import { ensureBrowserAuthBootstrap, getInitialBrowserUser, isAutoLoginNeeded, browserShowSignIn } from './browser-auth'
import { readBrowserAudioNoteState, readBrowserTranscriptEntries } from './browser-audio-notes'
import { readBrowserBillingState } from './browser-billing'
import { cloneStoreState, KNOWN_STORE_NAMESPACES, setStoreSnapshot, loadPersistedStore } from './browser-store'
import { parseBooleanFlag } from './browser-utils'

const buildEmptyStoreSnapshot = (): Record<string, Record<string, unknown>> => {
  return KNOWN_STORE_NAMESPACES.reduce(
    (snapshot, namespace) => {
      snapshot[namespace] = {}
      return snapshot
    },
    {} as Record<string, Record<string, unknown>>,
  )
}

export const buildInitialBrowserStoreSnapshot = (): Record<string, Record<string, unknown>> => {
  const disableAuth = parseBooleanFlag(import.meta.env.VITE_HIGHLIGHT_DISABLE_AUTH)
  const user = getInitialBrowserUser(disableAuth)
  const initialStores = buildEmptyStoreSnapshot()

  initialStores.__browser = { ready: true }
  initialStores['user.store'] = {
    ...initialStores['user.store'],
    'onboarding-complete': Date.now(),
  }
  initialStores['global.state'] = {
    ...initialStores['global.state'],
    sidepanel: { state: 'open' },
    versions: {
      app: window.highlight?.version ?? 'browser-dev',
    },
    user,
    permissions: {
      accessibilityAccess: true,
      microphoneAccess: true,
      screenCaptureStatus: 'authorized',
      systemAudioCaptureStatus: 'authorized',
    },
    'audio-note-state': readBrowserAudioNoteState(),
    'audio-note-transcript-entries': readBrowserTranscriptEntries(),
  }

  if (user) {
    initialStores['user.store'] = {
      ...initialStores['user.store'],
      profileEmail: user.email,
    }
  }

  const persistedSettings = loadPersistedStore('settings.store')
  initialStores['settings.store'] = {
    ...initialStores['settings.store'],
    'dev-mode': parseBooleanFlag(import.meta.env.VITE_HIGHLIGHT_BROWSER_DEV_MODE ?? 'true'),
    'audio-features-enabled': true,
    'audio-notes.transcription-method': 'cloud',
    ...(persistedSettings || {}),
  }
  initialStores['billing.store'] = {
    ...initialStores['billing.store'],
    state: readBrowserBillingState(),
  }
  // The browser runtime has no LaunchDarkly client, so nothing else would ever
  // turn the pricing revamp on. Seed it: the revamp is the only edition sold, and
  // browser-billing.ts carries its meeting-hour allowances.
  initialStores['flags.store'] = {
    ...initialStores['flags.store'],
    [FEATURE_FLAGS.V2_FREE_TIER_PLAN_REVAMP]: true,
  }

  return initialStores
}

export const ensureInitialBrowserStoreSnapshot = (): void => {
  const windowWithStoreData = window as Window & {
    __INITIAL_STORE_DATA?: Promise<Record<string, Record<string, unknown>>>
  }
  if (windowWithStoreData.__INITIAL_STORE_DATA) {
    return
  }

  windowWithStoreData.__INITIAL_STORE_DATA = (async () => {
    try {
      await ensureBrowserAuthBootstrap()

      const initialStores = buildInitialBrowserStoreSnapshot()

      if (isAutoLoginNeeded()) {
        void browserShowSignIn()
      }

      for (const [namespace, state] of Object.entries(initialStores)) {
        setStoreSnapshot(namespace, state)
      }

      return cloneStoreState(initialStores)
    } catch (error) {
      console.error('[browser-runtime] Failed to build initial browser store snapshot', error)
      throw error
    }
  })()
}
